import React, { useEffect, useRef, useState } from 'react';
import { siteInfo, socialLinks, experienceLevels } from '../data/mock';
import { Mail, MapPin, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    heightWeight: '',
    preferredDateTime: '',
    interests: '',
    experienceLevel: '',
    hardLimits: '',
    softLimits: '',
    medicalConditions: '',
    triggersPhobias: '',
    references: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: response.data.message
        });
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          age: '',
          heightWeight: '',
          preferredDateTime: '',
          interests: '',
          experienceLevel: '',
          hardLimits: '',
          softLimits: '',
          medicalConditions: '',
          triggersPhobias: '',
          references: ''
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'There was an error submitting your application. Please try again or email directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "w-full bg-transparent border border-[#8B7355]/30 px-4 py-3 text-[#A69080] focus:border-[#D4A853] focus:outline-none transition-colors placeholder:text-[#8B7355]/50";
  const labelClasses = "block text-[#8B7355] text-sm tracking-[0.15em] uppercase mb-2";

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="bg-[#0a0908] py-16 md:py-24 lg:py-32 px-4 md:px-6 overflow-hidden"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Divider */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
            <div className="mx-4 w-2 h-2 rotate-45 border border-[#8B0000]" />
            <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
          </div>

          <h2 className="text-[#A69080] text-2xl md:text-3xl tracking-[0.3em] uppercase mb-4">
            Apply to Serve
          </h2>

          {/* Divider */}
          <div className="flex items-center justify-center mt-8">
            <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
            <div className="mx-4 w-2 h-2 rotate-45 border border-[#D4A853]" />
            <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
          </div>

          <p className="text-[#8B7355] mt-8 text-base md:text-lg">
            Interested in booking a session? Complete the application below.
          </p>
        </div>

        {/* Contact Info */}
        <div
          className={`flex flex-wrap justify-center gap-8 mb-16 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 text-[#A69080]">
            <MapPin className="w-5 h-5 text-[#D4A853]" />
            <span className="tracking-wider">{siteInfo.location}</span>
          </div>
          <div className="flex items-center gap-3 text-[#A69080]">
            <Mail className="w-5 h-5 text-[#D4A853]" />
            <span className="tracking-wider">MistressT-RexMonroe@proton.me</span>
          </div>
        </div>

        {/* Contact Form */}
        <div
          className={`transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1: Name & Email */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={labelClasses}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder="Your Name"
                />
              </div>
              <div>
                <label className={labelClasses}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder="your@email.com"
                />
              </div>
            </div>

            {/* Row 2: Age & Height/Weight */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={labelClasses}>Age *</label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  required
                  min="18"
                  className={inputClasses}
                  placeholder="Must be 18+"
                />
              </div>
              <div>
                <label className={labelClasses}>Height & Weight</label>
                <input
                  type="text"
                  name="heightWeight"
                  value={formData.heightWeight}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="e.g., 5'10, 180 lbs"
                />
              </div>
            </div>

            {/* Row 3: Preferred Date/Time & Experience Level */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={labelClasses}>Preferred Date & Time *</label>
                <input
                  type="text"
                  name="preferredDateTime"
                  value={formData.preferredDateTime}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder="e.g., Saturday afternoon, July 15th"
                />
              </div>
              <div>
                <label className={labelClasses}>Experience Level *</label>
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  required
                  className={`${inputClasses} cursor-pointer`}
                >
                  <option value="" className="bg-[#0a0908]">Select your experience level</option>
                  {experienceLevels.map((level) => (
                    <option key={level} value={level} className="bg-[#0a0908]">
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Interests */}
            <div>
              <label className={labelClasses}>Interests & Fetishes *</label>
              <textarea
                name="interests"
                value={formData.interests}
                onChange={handleChange}
                required
                rows="4"
                className={`${inputClasses} resize-none`}
                placeholder="List your interests for your session, specific fetishes and/or ideas you'd like to explore with me..."
              />
            </div>

            {/* Row 4: Hard Limits & Soft Limits */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={labelClasses}>Hard Limits *</label>
                <textarea
                  name="hardLimits"
                  value={formData.hardLimits}
                  onChange={handleChange}
                  required
                  rows="3"
                  className={`${inputClasses} resize-none`}
                  placeholder="Activities you absolutely will not engage in..."
                />
              </div>
              <div>
                <label className={labelClasses}>Soft Limits</label>
                <textarea
                  name="softLimits"
                  value={formData.softLimits}
                  onChange={handleChange}
                  rows="3"
                  className={`${inputClasses} resize-none`}
                  placeholder="Activities you're hesitant about but may consider..."
                />
              </div>
            </div>

            {/* Medical Conditions */}
            <div>
              <label className={labelClasses}>Medical Conditions, Allergies & Injuries</label>
              <textarea
                name="medicalConditions"
                value={formData.medicalConditions}
                onChange={handleChange}
                rows="3"
                className={`${inputClasses} resize-none`}
                placeholder="List any medical conditions, allergies and/or injuries that could impact your health & safety during a session..."
              />
            </div>

            {/* Triggers & Phobias */}
            <div>
              <label className={labelClasses}>Triggers, Phobias & Mental Health</label>
              <textarea
                name="triggersPhobias"
                value={formData.triggersPhobias}
                onChange={handleChange}
                rows="3"
                className={`${inputClasses} resize-none`}
                placeholder="Do you have any specific triggers, phobias or mental health challenges that I should know about in order to best support your wellbeing during a session?"
              />
            </div>

            {/* References */}
            <div>
              <label className={labelClasses}>References</label>
              <textarea
                name="references"
                value={formData.references}
                onChange={handleChange}
                rows="3"
                className={`${inputClasses} resize-none`}
                placeholder="Have you seen a Pro Domme before? If so, please provide details..."
              />
            </div>

            {/* Submit Button */}
            <div className="text-center pt-4">
              {submitStatus.message && (
                <div className={`mb-6 p-4 border ${
                  submitStatus.type === 'success' 
                    ? 'border-[#6B7B4B] text-[#6B7B4B] bg-[#6B7B4B]/10' 
                    : 'border-[#8B0000] text-[#8B0000] bg-[#8B0000]/10'
                }`}>
                  {submitStatus.message}
                </div>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`inline-flex items-center gap-3 px-12 py-4 border text-sm tracking-[0.2em] uppercase transition-all duration-300 ${
                  isSubmitting
                    ? 'border-[#8B7355] text-[#8B7355] cursor-not-allowed'
                    : 'border-[#D4A853] text-[#D4A853] hover:bg-[#D4A853] hover:text-[#0a0908]'
                }`}
              >
                {isSubmitting ? (
                  <>
                    Submitting...
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Submit Application
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Social Links */}
        <div
          className={`flex flex-wrap justify-center gap-4 md:gap-8 mt-12 md:mt-16 transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#8B7355] text-xs md:text-sm tracking-[0.15em] md:tracking-[0.2em] uppercase hover:text-[#D4A853] transition-colors relative group"
            >
              {social.name}
              <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#D4A853] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Contact;
